import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Mail, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "replied", label: "Replied" },
];

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/leads", {
      params: active === "all" ? {} : { status: active },
    });
    setLeads(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const setStatus = async (id, status) => {
    await api.put(`/leads/${id}`, { status });
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    setSelected(null);
    load();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/leads/export.csv`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-10 max-w-[1280px]" data-testid="admin-leads-list">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Inbox</div>
          <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em]">
            Pesan masuk · Leads
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 border border-[#e3e3e1] bg-white px-4 py-2.5 text-sm hover:bg-[#f3f3f0]"
            data-testid="leads-export-csv"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="leads-filter">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setActive(s.value)}
            data-testid={`leads-filter-${s.value}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${
              active === s.value
                ? "bg-[#16181c] text-white border-[#16181c]"
                : "bg-white text-[#16181c] border-[#e3e3e1] hover:bg-[#f3f3f0]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        <div className="bg-white border border-[#e3e3e1] max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="px-6 py-8 text-[#5d5f64]">Loading…</div>
          ) : leads.length === 0 ? (
            <div className="px-6 py-8 text-center text-[#5d5f64]">
              No leads yet. Submissions from the public site will appear here.
            </div>
          ) : (
            leads.map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  setSelected(l);
                  if (l.status === "new") setStatus(l.id, "read");
                }}
                className={`w-full text-left px-5 py-4 border-b border-[#e3e3e1] hover:bg-[#f3f3f0] ${
                  selected?.id === l.id ? "bg-[#f3f3f0]" : ""
                }`}
                data-testid={`lead-row-${l.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-medium text-[#16181c] truncate">{l.name}</div>
                  <StatusPill status={l.status} />
                </div>
                <div className="text-xs text-[#5d5f64] truncate">{l.email}</div>
                <div className="text-xs text-[#5d5f64] mt-2">{fmtDate(l.created_at)}</div>
                {l.project_type && (
                  <div className="text-xs text-[#16181c] mt-1">· {l.project_type}</div>
                )}
              </button>
            ))
          )}
        </div>

        <div className="bg-white border border-[#e3e3e1] p-8 min-h-[400px]">
          {selected ? (
            <div data-testid="lead-detail">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-1">
                    {fmtDate(selected.created_at)}
                  </div>
                  <h2 className="font-display font-semibold text-[#16181c] text-2xl tracking-[-0.01em]">
                    {selected.name}
                  </h2>
                </div>
                <StatusPill status={selected.status} />
              </div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6 text-sm">
                <Row label="Email">
                  <a href={`mailto:${selected.email}`} className="text-[#16181c] hover:underline">
                    {selected.email}
                  </a>
                </Row>
                {selected.phone && (
                  <Row label="Phone / WhatsApp">
                    <a
                      href={`tel:${selected.phone.replace(/\s/g, "")}`}
                      className="text-[#16181c] hover:underline"
                    >
                      {selected.phone}
                    </a>
                  </Row>
                )}
                {selected.project_type && (
                  <Row label="Project type">{selected.project_type}</Row>
                )}
              </dl>
              <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Message</div>
              <p
                className="text-[#16181c] whitespace-pre-wrap leading-relaxed"
                data-testid="lead-message"
              >
                {selected.message}
              </p>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <a
                  href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(
                    "Pertanyaan Anda untuk Cipta Andalan"
                  )}`}
                  className="inline-flex items-center gap-2 bg-[#16181c] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#5d5f64]"
                  onClick={() => setStatus(selected.id, "replied")}
                  data-testid="lead-reply"
                >
                  <Mail size={14} /> Reply by email
                </a>
                <button
                  onClick={() => setStatus(selected.id, "read")}
                  className="text-sm border border-[#e3e3e1] px-4 py-2.5 hover:bg-[#f3f3f0]"
                  data-testid="lead-mark-read"
                >
                  Mark as read
                </button>
                <button
                  onClick={() => setStatus(selected.id, "replied")}
                  className="text-sm border border-[#e3e3e1] px-4 py-2.5 hover:bg-[#f3f3f0]"
                  data-testid="lead-mark-replied"
                >
                  Mark as replied
                </button>
                <button
                  onClick={() => onDelete(selected.id)}
                  className="ml-auto inline-flex items-center gap-2 text-sm text-[#16181c] hover:text-[#5d5f64]"
                  data-testid="lead-delete"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-[#5d5f64]">
              Select a lead to view details.
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 text-xs text-[#5d5f64]">
        <Link to="/admin" className="hover:text-[#16181c]">← Back to dashboard</Link>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles =
    status === "new"
      ? "bg-[#16181c] text-white"
      : status === "replied"
      ? "bg-white text-[#16181c] border border-[#16181c]"
      : "bg-[#f3f3f0] text-[#5d5f64]";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${styles}`}>{status}</span>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-[#5d5f64] mb-1">{label}</dt>
      <dd className="text-[#16181c]">{children}</dd>
    </div>
  );
}
